import { Box, Button, TextField, Typography } from '@mui/material'
import type { ClipboardEvent } from 'react'
import { buildPastedValue } from './formUtils'

const handlePaste = (onChange: (v: string) => void) => (e: ClipboardEvent) => {
  e.preventDefault()
  onChange(buildPastedValue(e.target as HTMLTextAreaElement, e.clipboardData.getData('text')))
}

const resultsBoxSx = {
  maxHeight: 160,
  overflowY: 'auto',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
  fontFamily: 'monospace',
  fontSize: 13,
}

export type MccTabProps = {
  loaded: string[]
  input: string
  search: string
  editMode: boolean
  parsed: string[]
  filtered: string[]
  hasInvalid: boolean
  onChange: (v: string) => void
  onSearchChange: (v: string) => void
  onEditModeToggle: () => void
}

export type MidsTabProps = {
  loaded: string[]
  input: string
  search: string
  editMode: boolean
  parsed: string[]
  filtered: string[]
  hasInvalid: boolean
  onChange: (v: string) => void
  onSearchChange: (v: string) => void
  onEditModeToggle: () => void
}

export type RulesTabProps = {
  expression?: string
  mcc: MccTabProps
  mids: MidsTabProps
}

export const RulesTab = ({ expression, mcc, mids }: RulesTabProps) => {
  const hasOriginalRuleData = mids.loaded.length > 0 || mcc.loaded.length > 0 || Boolean(expression)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {hasOriginalRuleData && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {expression && (
            <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(expression)}>
              Copy expression
            </Button>
          )}
          {mids.loaded.length > 0 && (
            <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(mids.loaded.join('\n'))}>
              Copy {mids.loaded.length} MIDs
            </Button>
          )}
          {mcc.loaded.length > 0 && (
            <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(mcc.loaded.join('\n'))}>
              Copy {mcc.loaded.length} MCCs
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            MCCs ({mcc.parsed.length})
          </Typography>
          <Button size="small" onClick={mcc.onEditModeToggle} aria-label={mcc.editMode ? 'Done MCCs' : 'Edit MCCs'}>
            {mcc.editMode ? 'Done' : 'Edit'}
          </Button>
        </Box>
        {mcc.editMode ? (
          <TextField
            label="MCCs"
            value={mcc.input}
            onChange={(e) => mcc.onChange(e.target.value)}
            onPaste={handlePaste(mcc.onChange)}
            multiline
            minRows={3}
            maxRows={5}
            error={mcc.hasInvalid}
            helperText={
              mcc.hasInvalid ? 'All values must be 4-digit integers, one per line' : `One MCC per line. ${mcc.parsed.length} valid MCC(s).`
            }
          />
        ) : (
          <>
            {mcc.parsed.length > 0 && (
              <TextField label="Search MCCs" size="small" value={mcc.search} onChange={(e) => mcc.onSearchChange(e.target.value)} />
            )}
            <Box sx={resultsBoxSx}>
              {mcc.parsed.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  No MCCs configured
                </Typography>
              ) : mcc.filtered.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {mcc.filtered.map((n) => (
                    <span key={n}>{n}</span>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  No matches
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            MIDs ({mids.parsed.length})
          </Typography>
          <Button size="small" onClick={mids.onEditModeToggle} aria-label={mids.editMode ? 'Done MIDs' : 'Edit MIDs'}>
            {mids.editMode ? 'Done' : 'Edit'}
          </Button>
        </Box>
        {mids.editMode ? (
          <TextField
            label="MIDs"
            value={mids.input}
            onChange={(e) => mids.onChange(e.target.value)}
            onPaste={handlePaste(mids.onChange)}
            multiline
            minRows={3}
            maxRows={5}
            error={mids.hasInvalid}
            helperText={
              mids.hasInvalid ? 'All values must be positive integers, one per line' : `One MID per line. ${mids.parsed.length} MID(s).`
            }
          />
        ) : (
          <>
            {mids.parsed.length > 0 && (
              <TextField label="Search MIDs" size="small" value={mids.search} onChange={(e) => mids.onSearchChange(e.target.value)} />
            )}
            <Box sx={resultsBoxSx}>
              {mids.parsed.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  No MIDs configured
                </Typography>
              ) : mids.filtered.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  {mids.filtered.map((mid) => (
                    <span key={mid}>{mid}</span>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  No matches
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
